import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Kanban as KanbanIcon, MagnifyingGlass, Package, ArrowRight, Storefront,
  CalendarBlank, Warning, Clock, ChatText, Paperclip, Receipt, TrendUp,
  Gear, WifiHigh, WifiSlash, CaretDown, CaretRight, FunnelSimple, RowsPlusBottom,
  User as UserIcon, PencilSimple,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { DateField } from '@/components/ui/date-time-field';
import { useT } from '@/i18n/LanguageContext';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { PRIORITY_BADGE, shizaiApi } from './shizaiApi';
import CategoryEditDialog from './CategoryEditDialog';
import { todayJst } from '@/utils/jstDate';

const NO_SUP = '__no_sup__'; // shadcn Select cannot hold an empty-string value

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const YEN = new Intl.NumberFormat('ja-JP');

const COLUMNS = [
  { key: 'open',     label: '依頼中',   colour: 'bg-sky-500',    ring: 'ring-sky-200',    icon: ClipboardIcon },
  { key: 'ordered',  label: '発注済',   colour: 'bg-amber-500',  ring: 'ring-amber-200',  icon: Receipt },
  { key: 'partial',  label: '一部入荷', colour: 'bg-orange-500', ring: 'ring-orange-200', icon: Package },
  { key: 'received', label: '入荷済',   colour: 'bg-emerald-500',ring: 'ring-emerald-200',icon: TrendUp },
  { key: 'closed',   label: '完了',     colour: 'bg-zinc-500',   ring: 'ring-zinc-200',   icon: ArrowRight },
];

function ClipboardIcon(props) { return <KanbanIcon {...props} />; }

// Which column transitions are legal (forward). Everything else pops a warning.
const VALID_FORWARD = {
  open: ['ordered'],
  ordered: ['partial', 'received'],
  partial: ['received'],
  received: ['closed'],
  closed: [],
};

export default function Kanban() {
  const { t } = useT();
  const navigate = useNavigate();
  const [data, setData] = useState({ columns: [], items: {}, summary: {}, wip_limits: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');       // v2: all|pending|approved|rejected|none
  const [groupBy, setGroupBy] = useState('none');                    // v2: none|supplier|order_name|order_category
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set()); // "{col}::{group}" keys
  const [draggingId, setDraggingId] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  // Multi-select — only cards in the 依頼中 (open) column can be selected for
  // bulk 発注 since bulk-order collapses N request lines into 1 PO.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  // Order-category master + quick-edit dialog state (Feb 2026): buyers can
  // slap 受注分類 on any WO/補充提案 REQ directly from the board without
  // leaving Kanban view.
  const [orderCategories, setOrderCategories] = useState([]);
  const [catEditItem, setCatEditItem] = useState(null);
  const [catBulkOpen, setCatBulkOpen] = useState(false);
  // Feb 2026: Supplier picker dialog — clicked from the supplier chip on any
  // Kanban card. Fetches the full REQ (with lines[]) and lets the buyer pick
  // per-line or apply one supplier to every 未発注 line.
  const [supEditItem, setSupEditItem] = useState(null);
  // WebSocket live-refresh state
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API_ROOT}/shizai/kanban`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('データの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Fetch suppliers once for the bulk-order dialog
  useEffect(() => {
    axios.get(`${API_ROOT}/mfg/suppliers`)
      .then((r) => setSuppliers(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // Fetch order categories once so the inline 受注分類 quick-edit dialog on
  // each Kanban card has options to pick from without extra round-trips.
  useEffect(() => {
    axios.get(`${API_ROOT}/shizai/order-categories`)
      .then((r) => setOrderCategories(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  // WebSocket subscription — auto re-fetch on any state change.
  useEffect(() => {
    const backend = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/^http/, 'ws');
    const url = `${backend}/api/shizai/kanban/ws`;
    let stop = false;
    let reconnectTimer = null;

    const connect = () => {
      if (stop) return;
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => setWsConnected(true);
        ws.onclose = () => {
          setWsConnected(false);
          if (!stop) reconnectTimer = setTimeout(connect, 3000);
        };
        ws.onerror = (err) => { console.warn('[kanban-ws] socket error', err); };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg && msg.event) load();
          } catch (err) { console.warn('[kanban-ws] bad payload', err); }
        };
      } catch (err) {
        console.warn('[kanban-ws] connect failed, retrying', err);
        reconnectTimer = setTimeout(connect, 3000);
      }
    };
    connect();
    return () => {
      stop = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (wsRef.current) try { wsRef.current.close(); } catch (err) { console.warn('[kanban-ws] close failed', err); }
    };
  }, [load]);

  // Client-side filter (search + priority + source + approval-status)
  const filtered = useMemo(() => {
    const rx = search.trim().toLowerCase();
    const out = { ...data.items };
    Object.keys(out).forEach((k) => {
      out[k] = (out[k] || []).filter((it) => {
        if (priorityFilter !== 'all' && it.priority !== priorityFilter) return false;
        if (sourceFilter !== 'all' && (it.source || 'buyer') !== sourceFilter) return false;
        if (approvalFilter !== 'all') {
          const st = it.approval_status;
          // 'none' matches items with no approval status (not_required / null / undefined / "")
          const bucket = (!st || st === 'not_required') ? 'none' : st;
          if (bucket !== approvalFilter) return false;
        }
        if (!rx) return true;
        const hay = [
          it.request_no, it.order_no, it.first_line_name, it.first_line_part_no,
          it.supplier_name, it.order_name, it.order_category_name, it.department_name,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(rx);
      });
    });
    return out;
  }, [data.items, search, priorityFilter, sourceFilter, approvalFilter]);

  // Approval-status counts across the WHOLE board (unfiltered) — for filter-pill counts
  const approvalCounts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, none: 0 };
    Object.values(data.items).flat().forEach((it) => {
      const st = it.approval_status;
      const bucket = (!st || st === 'not_required') ? 'none' : st;
      if (bucket in c) c[bucket] += 1;
    });
    return c;
  }, [data.items]);

  // WIP-limit overshoots (aggregate for the banner) — use RAW board counts
  // so applying a filter/search doesn't hide the warning. The banner is meant
  // to signal a real backlog problem regardless of what the user is looking at.
  const wipOvershoots = useMemo(() => {
    const overs = [];
    for (const col of COLUMNS) {
      const limit = data.wip_limits?.[col.key] || 0;
      const count = (data.items[col.key] || []).length;
      if (limit > 0 && count > limit) {
        overs.push({ col: col.key, label: col.label, count, limit, over: count - limit });
      }
    }
    return overs;
  }, [data.items, data.wip_limits]);

  // Group items within a column by supplier / order-name / order-category (client-side).
  // Returns [{key, label, items}] — a single "全て" group when groupBy=='none'.
  const groupItems = useCallback((items) => {
    if (groupBy === 'none') return [{ key: '__all', label: null, items }];
    const map = new Map();
    for (const it of items) {
      let k;
      let label;
      if (groupBy === 'supplier') {
        k = it.supplier_id || it.supplier_name || '__no_supplier';
        label = it.supplier_name || '（仕入先未指定）';
      } else if (groupBy === 'order_category') {
        k = it.order_category_id || it.order_category_code || '__no_cat';
        label = it.order_category_name
          ? (it.order_category_code ? `${it.order_category_code} ${it.order_category_name}` : it.order_category_name)
          : '（分類 未指定）';
      } else if (groupBy === 'work_order') {
        k = it.mfg_order_id || it.manufacturing_no || '__no_wo';
        label = it.manufacturing_no ? `WO ${it.manufacturing_no}` : '（作業指示なし）';
      } else { // order_name
        k = it.order_name || it.first_line_name || '__no_order';
        label = it.order_name || it.first_line_name || '（受注品名なし）';
      }
      if (!map.has(k)) map.set(k, { key: k, label, items: [] });
      map.get(k).items.push(it);
    }
    // Sort groups: biggest first, then alphabetical
    return Array.from(map.values()).sort((a, b) =>
      b.items.length - a.items.length || String(a.label).localeCompare(String(b.label))
    );
  }, [groupBy]);

  const toggleGroupCollapsed = (colKey, groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      const k = `${colKey}::${groupKey}`;
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const collapseAll = () => {
    if (groupBy === 'none') return;
    const keys = new Set();
    for (const col of COLUMNS) {
      for (const g of groupItems(filtered[col.key] || [])) {
        keys.add(`${col.key}::${g.key}`);
      }
    }
    setCollapsedGroups(keys);
  };
  const expandAll = () => setCollapsedGroups(new Set());

  const totalCount = Object.values(filtered).reduce((a, b) => a + b.length, 0);
  const partsCount = Object.values(data.items).flat().filter((x) => x.source === 'parts_request').length;

  const handleDragStart = (e, item) => {
    setDraggingId(item.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: item.id, from: item.column }));
  };
  const handleDragEnd = () => { setDraggingId(null); setHoverCol(null); };

  const handleDrop = (e, targetCol) => {
    e.preventDefault();
    setHoverCol(null);
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData('text/plain')); } catch { return; }
    const { id, from } = payload;
    if (!id || from === targetCol) return;
    // Find the item
    const item = (data.items[from] || []).find((x) => x.id === id);
    if (!item) return;
    const legal = (VALID_FORWARD[from] || []).includes(targetCol);
    if (!legal) {
      toast.error(`${colLabel(from)} → ${colLabel(targetCol)} の直接遷移はできません`);
      return;
    }
    // Trigger the appropriate action for the transition
    if (targetCol === 'ordered') {
      // Same as clicking 発注 on request → navigate to Orders with prefilled request
      toast.success('発注を作成', { description: item.request_no });
      navigate('/shizai/orders', { state: { request: mapItemToRequest(item) } });
    } else if (targetCol === 'partial' || targetCol === 'received') {
      if (!item.order_id) {
        toast.error('この依頼は未発注です');
        return;
      }
      navigate('/shizai/receiving', { state: { orderId: item.order_id, orderNo: item.order_no } });
    } else if (targetCol === 'closed') {
      toast.info('完了への移動は請求書照合から実行してください');
    }
  };

  const colLabel = (k) => COLUMNS.find((c) => c.key === k)?.label || k;

  return (
    <div className="space-y-4 page-enter" data-testid="shizai-kanban">
      {/* Header — the board's only job is to track the workflow visually.
          Administration (WIP上限) lives in 資材 › 設定. */}
      <PageHeader
        testid="kanban-header"
        title="かんばん"
        description={`依頼から入荷までの進行状況を列で追跡します（${totalCount}件）`}
        secondary={(
          <>
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md border ${
                wsConnected ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-zinc-100 text-zinc-700 border-zinc-200'
              }`}
              title={wsConnected ? 'ライブ更新: 接続中' : 'ライブ更新: 未接続（3秒ごとに再接続します）'}
              data-testid="kanban-ws-status"
            >
              {wsConnected ? <WifiHigh size={11} weight="bold" /> : <WifiSlash size={11} weight="bold" />}
              {wsConnected ? 'LIVE' : 'OFFLINE'}
            </span>
            <Button size="sm" variant="outline" onClick={load} data-testid="kanban-refresh" className="h-10">
              再読込
            </Button>
          </>
        )}
        menuItems={[
          { label: 'WIP上限の設定', icon: <Gear size={16} weight="duotone" />, onClick: () => navigate('/shizai/settings'), testid: 'kanban-wip-settings' },
        ]}
      />

      {/* Bulk-action bar — visible only when >=1 open card selected */}
      {selected.size > 0 && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap shadow-sm" data-testid="kanban-bulk-bar">
          <span className="text-sm font-medium text-zinc-900">選択中 {selected.size}件</span>
          <span className="text-xs text-zinc-500">選択された依頼をまとめて操作</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}
            data-testid="kanban-bulk-clear" className="h-9">
            選択解除
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCatBulkOpen(true)}
            data-testid="kanban-bulk-cat-btn"
            className="h-9 border-sky-300 text-sky-700 hover:bg-sky-50">
            分類を一括設定 ({selected.size})
          </Button>
          <Button size="sm" onClick={() => setBulkOpen(true)}
            data-testid="kanban-bulk-order-btn"
            className="h-9 bg-teal-700 hover:bg-teal-800 shadow-sm">
            一括発注 ({selected.size})
            <ArrowRight size={13} className="ml-1" weight="bold" />
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-lg">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="REQ番号 / 品名 / 品番 / 仕入先 / 部署"
            data-testid="kanban-search" className="h-9 pl-8" />
        </div>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger data-testid="kanban-priority-filter" className="h-9 w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">優先度: すべて</SelectItem>
            <SelectItem value="urgent">緊急</SelectItem>
            <SelectItem value="high">高</SelectItem>
            <SelectItem value="normal">普通</SelectItem>
            <SelectItem value="low">低</SelectItem>
          </SelectContent>
        </Select>
        {partsCount > 0 && (
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger data-testid="kanban-source-filter" className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">ソース: すべて</SelectItem>
              <SelectItem value="parts_request">部品依頼のみ ({partsCount})</SelectItem>
              <SelectItem value="buyer">内部のみ</SelectItem>
            </SelectContent>
          </Select>
        )}
        {/* v2: Approval-status filter */}
        <Select value={approvalFilter} onValueChange={setApprovalFilter}>
          <SelectTrigger data-testid="kanban-approval-filter" className="h-9 w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">承認: すべて</SelectItem>
            <SelectItem value="pending">承認待ち ({approvalCounts.pending})</SelectItem>
            <SelectItem value="approved">承認済 ({approvalCounts.approved})</SelectItem>
            <SelectItem value="rejected">却下 ({approvalCounts.rejected})</SelectItem>
            <SelectItem value="none">承認不要 ({approvalCounts.none})</SelectItem>
          </SelectContent>
        </Select>
        {/* v2: Group-by */}
        <Select value={groupBy} onValueChange={(v) => { setGroupBy(v); setCollapsedGroups(new Set()); }}>
          <SelectTrigger data-testid="kanban-group-by" className="h-9 w-44">
            <RowsPlusBottom size={13} className="mr-1.5 text-zinc-400 shrink-0" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">グループ化なし</SelectItem>
            <SelectItem value="supplier">仕入先で</SelectItem>
            <SelectItem value="order_name">受注品名で</SelectItem>
            <SelectItem value="order_category">分類で</SelectItem>
            <SelectItem value="work_order">作業指示で</SelectItem>
          </SelectContent>
        </Select>
        {groupBy !== 'none' && (
          <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden text-xs">
            <button type="button" onClick={collapseAll}
              data-testid="kanban-collapse-all"
              className="px-2.5 py-1.5 text-zinc-600 hover:bg-zinc-50 border-r border-zinc-200 inline-flex items-center gap-1">
              <CaretRight size={11} />全て折りたたむ
            </button>
            <button type="button" onClick={expandAll}
              data-testid="kanban-expand-all"
              className="px-2.5 py-1.5 text-zinc-600 hover:bg-zinc-50 inline-flex items-center gap-1">
              <CaretDown size={11} />全て展開
            </button>
          </div>
        )}
      </div>

      {/* v2: WIP-limit aggregate banner (visible only when any column is over its limit) */}
      {wipOvershoots.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-4 py-2.5 flex items-center gap-3 flex-wrap"
          data-testid="kanban-wip-banner">
          <Warning size={18} weight="fill" className="text-red-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-red-900">
              WIP上限超過：{wipOvershoots.length}列
            </div>
            <div className="text-xs text-red-700 mt-0.5 flex items-center gap-2 flex-wrap">
              {wipOvershoots.map((o) => (
                <span key={o.col} data-testid={`kanban-wip-banner-${o.col}`}
                  className="inline-flex items-center gap-1 bg-white border border-red-200 rounded-md px-2 py-0.5 font-mono tabular-nums">
                  <span className="font-semibold">{o.label}</span>
                  <span className="text-red-700">{o.count}/{o.limit}</span>
                  <span className="text-red-500 text-[10px]">+{o.over}超</span>
                </span>
              ))}
            </div>
          </div>
          <span className="text-xs text-zinc-500 hidden sm:inline">仕掛数が上限を超えています。処理を進めるか、設定でWIP上限を調整してください。</span>
        </div>
      )}

      {/* Board */}
      {loading ? (
        <div className="p-12 text-center text-sm text-zinc-400">読み込み中...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="kanban-board">
          {COLUMNS.map((col) => {
            const items = filtered[col.key] || [];
            const summary = data.summary?.[col.key] || { count: 0, total_amount: 0 };
            const isHover = hoverCol === col.key;
            const wipLimit = data.wip_limits?.[col.key] || 0;
            const overLimit = wipLimit > 0 && items.length > wipLimit;
            return (
              <div
                key={col.key}
                data-testid={`kanban-col-${col.key}`}
                onDragOver={(e) => { e.preventDefault(); setHoverCol(col.key); }}
                onDragLeave={() => setHoverCol((c) => (c === col.key ? null : c))}
                onDrop={(e) => handleDrop(e, col.key)}
                className={`rounded-xl border bg-zinc-50/60 min-h-[400px] flex flex-col transition-colors ${
                  isHover ? 'border-rose-400 ring-2 ring-rose-200 bg-rose-50/50'
                  : overLimit ? 'border-red-300 ring-1 ring-red-100'
                  : 'border-zinc-200'
                }`}
              >
                <div className={`px-3 py-2 flex items-center justify-between border-b border-zinc-200 rounded-t-xl bg-white`}>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${col.colour}`} />
                    <h3 className="text-sm font-semibold text-zinc-800">{col.label}</h3>
                    <span className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded font-semibold ${
                      overLimit ? 'bg-red-100 text-red-700' : 'bg-zinc-100 text-zinc-500'
                    }`} data-testid={`kanban-col-count-${col.key}`}>
                      {items.length}{wipLimit > 0 ? `/${wipLimit}` : ''}
                    </span>
                    {overLimit && (
                      <span className="text-[9px] font-semibold text-red-700 inline-flex items-center gap-0.5"
                        title="WIP上限超過" data-testid={`kanban-wip-warn-${col.key}`}>
                        <Warning size={10} weight="fill" />超過
                      </span>
                    )}
                  </div>
                  {summary.total_amount > 0 && (
                    <span className="text-[10px] font-semibold text-zinc-500 tabular-nums">¥{YEN.format(summary.total_amount)}</span>
                  )}
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-320px)]">
                  {items.length === 0 ? (
                    <p className="text-[11px] text-zinc-400 text-center py-6">依頼なし</p>
                  ) : groupItems(items).map((grp) => {
                    const gkey = `${col.key}::${grp.key}`;
                    const collapsed = collapsedGroups.has(gkey);
                    const showHeader = groupBy !== 'none' && grp.label !== null;
                    return (
                      <div key={grp.key} data-testid={`kanban-group-${col.key}-${grp.key}`}>
                        {showHeader && (
                          <button
                            type="button"
                            onClick={() => toggleGroupCollapsed(col.key, grp.key)}
                            data-testid={`kanban-group-toggle-${col.key}-${grp.key}`}
                            className="w-full flex items-center gap-1.5 px-1.5 py-1 mb-1 bg-white/70 hover:bg-white border border-zinc-200 rounded-md text-left transition-colors"
                          >
                            {collapsed ? (
                              <CaretRight size={12} weight="bold" className="text-zinc-500 shrink-0" />
                            ) : (
                              <CaretDown size={12} weight="bold" className="text-zinc-500 shrink-0" />
                            )}
                            <span className="text-[11px] font-semibold text-zinc-700 truncate flex-1" title={grp.label}>
                              {grp.label}
                            </span>
                            <span className="text-[10px] tabular-nums font-mono text-zinc-500 shrink-0">
                              {grp.items.length}
                            </span>
                          </button>
                        )}
                        {!collapsed && grp.items.map((it) => (
                          <KanbanCard
                            key={it.id}
                            item={it}
                            dragging={draggingId === it.id}
                            onDragStart={(e) => handleDragStart(e, it)}
                            onDragEnd={handleDragEnd}
                            onClick={() => navigate('/shizai/requests')}
                            selectable={col.key === 'open'}
                            selected={selected.has(it.id)}
                            onSelectToggle={(v) => setSelected((prev) => {
                              const next = new Set(prev);
                              if (v) next.add(it.id); else next.delete(it.id);
                              return next;
                            })}
                            onEditCategory={setCatEditItem}
                            onEditSupplier={setSupEditItem}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <BulkOrderDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        selectedIds={[...selected]}
        openItems={data.items?.open || []}
        suppliers={suppliers}
        onSuccess={(count, orderNo) => {
          toast.success(`一括発注 完了`, { description: `${count}件を発注 ${orderNo}にまとめました` });
          setSelected(new Set());
          setBulkOpen(false);
          load();
        }}
      />

      {/* Inline 受注分類 quick-edit dialog (Feb 2026) — driven by clicks on
          the「+ 分類」or filled-category chip on any KanbanCard. Reload
          triggered via bumping the WS-driven load counter. */}
      {catEditItem && (
        <CategoryEditDialog
          req={catEditItem}
          categories={orderCategories}
          onClose={() => setCatEditItem(null)}
          onSaved={() => { setCatEditItem(null); load(); }}
        />
      )}

      {/* Bulk 受注分類 assign — kicked off from the bulk-select actionbar */}
      {catBulkOpen && (() => {
        const ids = Array.from(selected);
        const openItems = (data.items?.open) || [];
        const chosen = openItems.filter((it) => selected.has(it.id));
        const hint = chosen.slice(0, 3).map((it) => it.request_no).join('、')
          + (chosen.length > 3 ? ` 他${chosen.length - 3}件` : '');
        return (
          <CategoryEditDialog
            bulk={{ ids, count: ids.length, hint, orderType: 'purchase' }}
            categories={orderCategories}
            onClose={() => setCatBulkOpen(false)}
            onSaved={() => { setCatBulkOpen(false); setSelected(new Set()); load(); }}
          />
        );
      })()}

      {/* Feb 2026: Inline 仕入先 picker dialog — opened by clicking the supplier
          chip (or the "+ 仕入先" empty-state chip) on any Kanban card. */}
      {supEditItem && (
        <KanbanSupplierDialog
          item={supEditItem}
          suppliers={suppliers}
          onClose={() => setSupEditItem(null)}
          onSaved={() => { setSupEditItem(null); load(); }}
        />
      )}
    </div>
  );
}

function KanbanCard({ item, dragging, onDragStart, onDragEnd, onClick, selectable, selected, onSelectToggle, onEditCategory, onEditSupplier }) {
  const isParts = item.source === 'parts_request';
  const overdue = item.needed_by && item.needed_by < todayJst() && item.column !== 'closed';
  const priorityCls = PRIORITY_BADGE[item.priority] || PRIORITY_BADGE.normal;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      data-testid={`kanban-card-${item.id}`}
      data-request-no={item.request_no}
      data-source={item.source || 'buyer'}
      className={`bg-white rounded-lg border p-2.5 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-all ${
        dragging ? 'opacity-40 rotate-1' : ''
      } ${isParts ? 'border-orange-200 ring-1 ring-orange-100' : 'border-zinc-200'} ${
        overdue ? 'border-l-4 border-l-red-500' : ''
      } ${selected ? 'ring-2 ring-rose-400 border-rose-300' : ''}`}
    >
      {/* Row 1: checkbox + request_no + badges */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {selectable && (
          <span
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            draggable={false}
          >
            <Checkbox
              checked={selected}
              onCheckedChange={(v) => onSelectToggle?.(!!v)}
              data-testid={`kanban-card-select-${item.id}`}
              className="h-3.5 w-3.5"
              aria-label="選択"
            />
          </span>
        )}
        <span className="font-mono text-[10px] text-zinc-500">{item.request_no}</span>
        {isParts && (
          <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-[9px] px-1 py-0">
            <Storefront size={9} weight="bold" className="mr-0.5" />依頼
          </Badge>
        )}
        <Badge variant="outline" className={`${priorityCls} text-[9px] px-1 py-0`}>
          {item.priority === 'urgent' && '緊急'}
          {item.priority === 'high' && '高'}
          {item.priority === 'normal' && '普通'}
          {item.priority === 'low' && '低'}
        </Badge>
        {item.department_name && (
          <span className="inline-flex items-center px-1 py-0 rounded text-[9px] font-semibold text-white"
            style={{ backgroundColor: item.department_color || '#a1a1aa' }}>
            {item.department_code || item.department_name}
          </span>
        )}
      </div>

      {/* Row 2: 品名 — WO-generated REQs show 「<製品名> 用の部品」 title so
          buyers instantly know what build the parts are for, instead of the
          alphabetically-first component's name. */}
      {item.product_name ? (
        <>
          <div className="mt-1 text-xs font-medium text-zinc-800 truncate"
               data-testid={`kanban-product-name-${item.id}`}
               title={`${item.product_name} 用の部品`}>
            {item.product_name} 用の部品
          </div>
          <div className="text-[10px] text-zinc-500">{item.line_count || 0} 明細</div>
        </>
      ) : (
        <>
          <div className="mt-1 text-xs font-medium text-zinc-800 truncate" title={item.first_line_name}>
            {item.first_line_name || item.order_name || '—'}
          </div>
          {item.line_count > 1 && (
            <div className="text-[10px] text-zinc-500">他 {item.line_count - 1} 件</div>
          )}
        </>
      )}

      {/* Row 3: 受注 label + supplier + 作業指示 + 進捗 + 受注分類 (editable) */}
      {(item.order_name || item.order_category_name || item.manufacturing_no || item.is_partially_ordered || (item.line_suppliers || []).length > 1 || (onEditCategory && item.request_status !== 'closed')) && (
        <div className="mt-1 flex items-center gap-1 flex-wrap">
          {item.order_name && (
            <span className="px-1 py-0 rounded bg-orange-50 text-orange-700 border border-orange-200 text-[9px] truncate max-w-[140px]"
              title={item.order_name}>{item.order_name}</span>
          )}
          {/* Editable 受注分類 chip. Feb 2026: mirrors the Requests-list UX so
              buyers can slap on 分類 from the board. Clickable only when
              onEditCategory prop is present (i.e. always on this board — a
              REQ is never fully-closed from Kanban view). */}
          {item.order_category_name ? (
            onEditCategory ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onEditCategory(item); }}
                onMouseDown={(e) => e.stopPropagation()}
                draggable={false}
                className="px-1 py-0 rounded bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 hover:border-sky-300 text-[9px] transition-colors"
                data-testid={`kanban-order-cat-${item.id}`}
                title={`受注分類: ${item.order_category_name} (クリックで変更)`}
              >
                {item.order_category_code || item.order_category_name}
              </button>
            ) : (
              <span className="px-1 py-0 rounded bg-sky-50 text-sky-700 border border-sky-200 text-[9px]">
                {item.order_category_code || item.order_category_name}
              </span>
            )
          ) : onEditCategory && item.request_status !== 'closed' && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEditCategory(item); }}
              onMouseDown={(e) => e.stopPropagation()}
              draggable={false}
              className="px-1 py-0 rounded border border-dashed border-zinc-300 text-zinc-500 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-300 text-[9px] transition-colors inline-flex items-center gap-0.5"
              data-testid={`kanban-order-cat-add-${item.id}`}
              title="受注分類を設定"
            >
              + 分類
            </button>
          )}
          {item.manufacturing_no && (
            <span className="px-1 py-0 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 text-[9px] font-mono" title={`作業指示: ${item.manufacturing_no}`}>
              WO {item.manufacturing_no}
            </span>
          )}
          {/* Multi-supplier hint: shown even before any PO is created */}
          {(item.line_suppliers || []).length > 1 && (
            <span className="px-1 py-0 rounded bg-purple-50 text-purple-700 border border-purple-200 text-[9px] font-semibold"
              data-testid={`kanban-multisup-${item.id}`}
              title={`未発注の仕入先 ${(item.line_suppliers || []).length}社`}>
              {(item.line_suppliers || []).length}社
            </span>
          )}
          {/* Partial-ordered progress: shown when some lines are already ordered */}
          {(item.lines_total_count || 0) > 1 && (item.lines_ordered_count || 0) > 0 && (item.lines_ordered_count < item.lines_total_count) && (
            <span className="px-1 py-0 rounded bg-amber-100 text-amber-800 border border-amber-300 text-[9px] font-semibold"
              data-testid={`kanban-partial-${item.id}`}
              title="一部の明細のみ発注済">
              {item.lines_ordered_count}/{item.lines_total_count} 発注済
            </span>
          )}
          {/* Master-linked signal: green chip when every line is linked to
              品目マスター (item_id set) so downstream stock/invoice matching is
              clean. Grey chip when only some are linked. Nothing when 0. */}
          {(item.lines_total_count || 0) > 0 && (item.linked_lines_count || 0) > 0 && (
            <span
              className={`px-1 py-0 rounded text-[9px] font-semibold ${
                item.linked_lines_count === item.lines_total_count
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-zinc-100 text-zinc-600 border border-zinc-300'
              }`}
              data-testid={`kanban-linked-${item.id}`}
              title={item.linked_lines_count === item.lines_total_count
                ? '全ての明細が品目マスターとリンク済 — 検収・請求・在庫に自動反映されます'
                : 'マスター未リンクの明細があります — 検収時に手動照合が必要です'}>
              マスター {item.linked_lines_count}/{item.lines_total_count}
            </span>
          )}
        </div>
      )}

      {/* Row 4: needed_by + supplier + amount */}
      <div className="mt-1.5 flex items-center gap-2 text-[10px] text-zinc-500 flex-wrap">
        {item.needed_by && (
          <span className={`inline-flex items-center gap-0.5 ${overdue ? 'text-red-600 font-semibold' : ''}`}>
            <CalendarBlank size={9} weight="bold" />{item.needed_by}
            {overdue && <Warning size={9} weight="fill" className="ml-0.5" />}
          </span>
        )}
        {item.order_no && (
          <span className="font-mono text-emerald-600 truncate">{item.order_no}</span>
        )}
        {item.approval_status === 'pending' && (
          <span className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-amber-100 text-amber-700 border border-amber-300 text-[9px] font-semibold"
            data-testid={`kanban-approval-${item.id}`}
            title="承認待ち">
            <Clock size={9} weight="fill" />承認待ち
          </span>
        )}
        {item.approval_status === 'approved' && (
          <span className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-emerald-100 text-emerald-700 border border-emerald-300 text-[9px] font-semibold"
            data-testid={`kanban-approval-${item.id}`}
            title="承認済">
            ✓ 承認済
          </span>
        )}
        {item.approval_status === 'rejected' && (
          <span className="inline-flex items-center gap-0.5 px-1 py-0 rounded bg-red-100 text-red-700 border border-red-300 text-[9px] font-semibold"
            data-testid={`kanban-approval-${item.id}`}
            title="却下">
            ✕ 却下
          </span>
        )}
        {/* Feb 2026: Clickable 仕入先 chip — opens the picker dialog. Shown as
            a warning-styled chip when supplier is UNSET for any 未発注 line
            in the REQ, so buyers can spot & fix it directly from Kanban. */}
        {onEditSupplier && item.request_status !== 'closed' && (() => {
          // 未発注 lines still on this REQ. If any of them has NO supplier,
          // the card should surface the empty state loudly.
          const openLinesTotal = (item.lines_total_count || 0) - (item.lines_ordered_count || 0);
          const openLinesWithSup = (item.line_suppliers || []).length;
          const missingSup = openLinesTotal > 0 && openLinesWithSup === 0 && !item.supplier_name;
          const partiallyMissing = openLinesTotal > openLinesWithSup && openLinesWithSup > 0;
          const label = item.supplier_name
            ? item.supplier_name
            : (openLinesTotal > 0 ? '仕入先未指定' : (item.supplier_name || null));
          if (!label && openLinesTotal === 0) return null;
          const cls = missingSup
            ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100 animate-pulse-subtle'
            : partiallyMissing
              ? 'bg-orange-50 text-orange-800 border-orange-300 hover:bg-orange-100'
              : 'bg-zinc-100 text-zinc-700 border-zinc-200 hover:bg-zinc-200';
          return (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEditSupplier(item); }}
              onMouseDown={(e) => e.stopPropagation()}
              draggable={false}
              className={`inline-flex items-center gap-0.5 px-1 py-0 rounded border truncate max-w-[140px] text-[9px] font-semibold transition-colors ${cls}`}
              data-testid={`kanban-supplier-chip-${item.id}`}
              title={missingSup ? '仕入先を選択してください' : partiallyMissing ? '一部の明細は仕入先未指定です' : '仕入先を変更'}
            >
              <Storefront size={9} weight="bold" />
              <span className="truncate">{label}</span>
              <PencilSimple size={8} weight="bold" className="opacity-60 shrink-0" />
            </button>
          );
        })()}
        {/* Released-by name for WO-generated REQs — helps buyers know which
            production person to contact if there's a question. */}
        {item.manufacturing_no && item.requested_by && (
          <span className="inline-flex items-center gap-0.5 truncate max-w-[100px] text-zinc-500"
                data-testid={`kanban-released-by-${item.id}`}
                title={`作業指示を開始: ${item.requested_by}`}>
            <UserIcon size={9} weight="bold" />{item.requested_by}
          </span>
        )}
        {/* Ordered-by name — who placed the PO. Shown on ANY card that has an
            attached PO so buyers know who to ping about a live purchase order. */}
        {item.order_id && item.ordered_by && (
          <span className="inline-flex items-center gap-0.5 truncate max-w-[110px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-1"
                data-testid={`kanban-ordered-by-${item.id}`}
                title={`発注担当: ${item.ordered_by}`}>
            <UserIcon size={9} weight="bold" />発注: {item.ordered_by}
          </span>
        )}
        {item.order_total > 0 && (
          <span className="tabular-nums font-semibold text-zinc-700">¥{YEN.format(item.order_total)}</span>
        )}
        {item.has_attachment && (
          <Paperclip size={9} weight="bold" className="text-rose-500" />
        )}
      </div>

      {/* Row 5: touch chip */}
      {item.days_since_touch >= 3 && item.column !== 'closed' && (
        <div className="mt-1 inline-flex items-center gap-0.5 text-[9px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1">
          <Clock size={9} weight="bold" />{item.days_since_touch}日 未更新
        </div>
      )}
    </div>
  );
}

// Map a Kanban item back to a shape the /shizai/orders new-PO flow expects.
function mapItemToRequest(it) {
  return {
    id: it.id,
    request_no: it.request_no,
    department_id: it.department_id,
    priority: it.priority,
    needed_by: it.needed_by,
    // The full lines are re-fetched by /shizai/orders when it opens; we only need id here
  };
}

/**
 * Bulk-order dialog. Collapses N selected 発注依頼 into ONE 発注 (PO) against
 * a single supplier — the buyer chooses supplier + optional 納期/納入場所 and
 * the backend concatenates all the request lines into one PO body.
 */
function BulkOrderDialog({ open, onClose, selectedIds, openItems, suppliers, onSuccess }) {
  const [supplierId, setSupplierId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [supSearch, setSupSearch] = useState('');

  useEffect(() => {
    if (open) {
      setSupplierId('');
      setDueDate('');
      setNotes('');
      setSupSearch('');
    }
  }, [open]);

  const selectedItems = openItems.filter((it) => selectedIds.includes(it.id));
  const filteredSuppliers = supSearch
    ? suppliers.filter((s) => (s.name || '').toLowerCase().includes(supSearch.toLowerCase()))
    : suppliers.slice(0, 100);

  const submit = async () => {
    if (!supplierId) { toast.error('仕入先を選択してください'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_ROOT}/shizai/orders/bulk`, {
        supplier_id: supplierId,
        request_ids: selectedIds,
        due_date: dueDate || null,
        notes: notes || null,
      });
      onSuccess?.(r.data.request_count, r.data.order?.order_no);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '一括発注に失敗しました');
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="kanban-bulk-dialog">
        <SheetHeader>
          <SheetTitle>一括発注 ({selectedIds.length}件)</SheetTitle>
          <SheetDescription>
            選択した依頼を1件の発注（PO）にまとめます。同一仕入先の依頼を選択してください。
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-5">
          {/* Selected items preview */}
          <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
            <p className="text-xs font-semibold text-zinc-600 mb-2">選択された依頼</p>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {selectedItems.map((it) => (
                <div key={it.id} className="flex items-center gap-2 text-xs" data-testid={`kanban-bulk-item-${it.id}`}>
                  <span className="font-mono text-zinc-500 w-24">{it.request_no}</span>
                  <span className="truncate flex-1">{it.first_line_name || '—'}</span>
                  <span className="text-zinc-500 tabular-nums">{it.total_qty}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Supplier picker */}
          <div>
            <Label className="text-xs font-semibold text-zinc-600">仕入先<span className="text-red-500 ml-0.5">*</span></Label>
            <Input value={supSearch} onChange={(e) => setSupSearch(e.target.value)}
              placeholder="検索: 名前で絞り込み"
              data-testid="kanban-bulk-supplier-search"
              className="h-9 mt-1 mb-1" />
            <div className="rounded-lg border border-zinc-200 max-h-48 overflow-y-auto bg-white" data-testid="kanban-bulk-supplier-list">
              {filteredSuppliers.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSupplierId(s.id)}
                  data-testid={`kanban-bulk-supplier-${s.id}`}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-rose-50 border-b border-zinc-200 last:border-b-0 ${
                    supplierId === s.id ? 'bg-rose-100 text-rose-800 font-semibold' : ''
                  }`}
                >
                  {s.name}
                  {s.contact_email && <span className="text-zinc-500 ml-2 text-[10px]">{s.contact_email}</span>}
                </button>
              ))}
              {filteredSuppliers.length === 0 && (
                <p className="text-xs text-zinc-400 text-center py-4">該当なし</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-zinc-600">納期</Label>
              <DateField value={dueDate} onChange={setDueDate} className="mt-1" inputClassName="h-9" testId="kanban-bulk-due-date" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">備考</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder="任意"
                data-testid="kanban-bulk-notes"
                className="h-9 mt-1" />
            </div>
          </div>
        </div>

        <SheetFooter className="mt-6 flex-row justify-end gap-2">
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={submit} disabled={busy || !supplierId}
            data-testid="kanban-bulk-submit">
            {busy ? '発注中…' : '発注を作成'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}


/**
 * KanbanSupplierDialog (Feb 2026) — inline 仕入先 picker for any Kanban card.
 * Opens on chip-click; fetches the full REQ (with lines[]) and lets the buyer
 *  (1) pick a supplier per un-ordered line, and
 *  (2) bulk-apply one supplier to every 未発注 line.
 * 発注済 lines are shown read-only with an amber note — use the Requests page
 * for the confirm-first PO-cancel flow.
 */
function KanbanSupplierDialog({ item, suppliers, onClose, onSaved }) {
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supByIdx, setSupByIdx] = useState({});           // index → chosen supplier_id (edits)
  const [bulkSup, setBulkSup] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    shizaiApi.request(item.id)
      .then((data) => {
        if (cancelled) return;
        setReq(data);
        // Seed the edit state with the current per-line supplier so a "no-op
        // save" doesn't overwrite anything.
        const init = {};
        (data.lines || []).forEach((ln, i) => { init[i] = ln.supplier_id || ''; });
        setSupByIdx(init);
      })
      .catch((e) => { toast.error(e?.response?.data?.detail || '依頼の取得に失敗しました'); onClose && onClose(); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [item.id, onClose]);

  const openLines = (req?.lines || [])
    .map((ln, i) => ({ ln, i }))
    .filter(({ ln }) => ln.line_status !== 'ordered');
  const orderedLines = (req?.lines || [])
    .map((ln, i) => ({ ln, i }))
    .filter(({ ln }) => ln.line_status === 'ordered');

  const applyBulk = () => {
    if (!bulkSup) return;
    setSupByIdx((prev) => {
      const next = { ...prev };
      openLines.forEach(({ i }) => { next[i] = bulkSup; });
      return next;
    });
  };

  const save = async () => {
    // Compute the diff and fire one patch per changed line.
    const changed = openLines
      .filter(({ ln, i }) => (supByIdx[i] || '') !== (ln.supplier_id || ''))
      .map(({ i }) => ({ i, supplier_id: supByIdx[i] || null }));
    if (changed.length === 0) { onClose && onClose(); return; }
    setBusy(true);
    try {
      // Sequential — the same list is being mutated; parallel would race.
      for (const { i, supplier_id } of changed) {
        await shizaiApi.patchLineSupplier(req.id, i, supplier_id);
      }
      toast.success(`${changed.length}明細の仕入先を更新しました`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '仕入先の更新に失敗しました');
    } finally { setBusy(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid={`kanban-supplier-dialog-${item.id}`}>
        <SheetHeader>
          <SheetTitle>
            仕入先の変更
            <span className="ml-2 font-mono text-xs text-zinc-500">{item.request_no}</span>
          </SheetTitle>
          <SheetDescription>
            未発注の明細ごとに仕入先を選択できます。一括で同じ仕入先を割り当てることもできます。
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-zinc-400">読み込み中…</div>
        ) : (
          <div className="space-y-4 mt-5">
            {/* Bulk-apply strip */}
            {openLines.length > 1 && (
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 flex items-center gap-2 flex-wrap"
                data-testid={`kanban-supplier-dialog-bulk-${item.id}`}>
                <span className="text-[11px] font-medium text-zinc-700 shrink-0">
                  未発注 {openLines.length}明細 に一括適用:
                </span>
                <Select value={bulkSup || NO_SUP} onValueChange={(v) => setBulkSup(v === NO_SUP ? '' : v)}>
                  <SelectTrigger className="h-9 max-w-[240px] text-xs bg-white" data-testid={`kanban-supplier-dialog-bulk-select-${item.id}`}>
                    <SelectValue placeholder="仕入先を選択..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_SUP}>仕入先を選択...</SelectItem>
                    {(suppliers || []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={applyBulk}
                  disabled={!bulkSup}
                  data-testid={`kanban-supplier-dialog-bulk-apply-${item.id}`}
                  className="h-9"
                >
                  適用
                </Button>
              </div>
            )}

            {/* Per-line pickers (未発注) */}
            <div className="space-y-2">
              {openLines.length === 0 && (
                <p className="text-xs text-zinc-500 italic">未発注の明細はありません。</p>
              )}
              {openLines.map(({ ln, i }) => (
                <div key={`${req.id}-ln-${i}`}
                     className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2"
                     data-testid={`kanban-supplier-dialog-line-${item.id}-${i}`}>
                  <span className="text-[10px] font-semibold text-zinc-400 shrink-0">#{i + 1}</span>
                  <span className="text-xs font-medium text-zinc-800 truncate flex-1" title={ln.name || ln.part_no}>
                    {ln.name || ln.part_no || '—'}
                  </span>
                  <span className="tabular-nums text-[11px] text-zinc-500 shrink-0">{ln.qty} {ln.uom}</span>
                  <Select
                    value={supByIdx[i] || NO_SUP}
                    onValueChange={(v) => setSupByIdx((p) => ({ ...p, [i]: v === NO_SUP ? '' : v }))}
                  >
                    <SelectTrigger
                      data-testid={`kanban-supplier-dialog-select-${item.id}-${i}`}
                      className={`h-9 w-[220px] text-xs ${supByIdx[i] ? 'bg-white' : 'border-amber-300 bg-amber-50 text-amber-800'}`}
                    >
                      <SelectValue placeholder="未指定" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_SUP}>未指定</SelectItem>
                      {(suppliers || []).map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {/* Ordered lines — read-only footnote */}
            {orderedLines.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11px] text-amber-900">
                <div className="font-semibold mb-1">発注済み明細 ({orderedLines.length}件)</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {orderedLines.map(({ ln, i }) => (
                    <li key={`${req.id}-oln-${i}`} className="truncate">
                      <span className="font-mono text-[10px] text-amber-700">#{i + 1}</span>{' '}
                      <span className="font-medium">{ln.name || ln.part_no || '—'}</span>
                      {ln.supplier_name && <span className="text-amber-700"> — {ln.supplier_name}</span>}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-[10px] text-amber-800">
                  ※ 発注済みの仕入先を変更するには、発注依頼一覧から確認ダイアログ経由で行なってください（発注は自動取消されます）。
                </p>
              </div>
            )}
          </div>
        )}

        <SheetFooter className="mt-6 flex-row justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid={`kanban-supplier-dialog-cancel-${item.id}`}>キャンセル</Button>
          <Button
            onClick={save}
            disabled={busy || loading || openLines.length === 0}
            data-testid={`kanban-supplier-dialog-save-${item.id}`}
          >
            {busy ? '保存中…' : '保存'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
